import {
  fetchPaymentHistory,
  fetchActiveSubscriptions,
  fetchRewards,
  checkHasSavedCard,
  type PaymentTransaction,
  type ActiveSubscription,
  type RewardsData,
} from "../services/AccountService";
import { getPreviousCleaners, type PreviousCleaner } from "../services/BookingService";
import { captureAppError } from "../observability/sentry";

export const PAYMENT_HISTORY_TTL_MS = 5 * 60 * 1000;  // 5 minutes
export const SUBSCRIPTIONS_TTL_MS = 150_000;            // 2.5 minutes

// ─── Payment history cache ────────────────────────────────────────────────────

let cachedHistory: {
  userId: string;
  payments: PaymentTransaction[];
  timestamp: number;
} | null = null;

/**
 * @param userId       Current user's firebase_id. Different user = fresh fetch.
 * @param ttlMs        Freshness window (default 5 min).
 * @param onBackground When provided and cache is stale, stale data is returned
 *                     immediately and a background fetch updates the caller via
 *                     this callback (stale-while-revalidate).
 */
export async function getCachedPaymentHistory(
  userId: string,
  ttlMs = PAYMENT_HISTORY_TTL_MS,
  onBackground?: (payments: PaymentTransaction[]) => void,
): Promise<{ payments: PaymentTransaction[]; error?: string | null }> {
  if (cachedHistory && cachedHistory.userId === userId) {
    const isStale = Date.now() - cachedHistory.timestamp >= ttlMs;
    if (!isStale) {
      return { payments: cachedHistory.payments };
    }
    if (onBackground) {
      const stale = cachedHistory.payments;
      const snapshotTimestamp = cachedHistory.timestamp;
      fetchPaymentHistory()
        .then((result) => {
          if (!result.error && Array.isArray(result.payments)) {
            if (cachedHistory?.timestamp === snapshotTimestamp && cachedHistory?.userId === userId) {
              cachedHistory = { userId, payments: result.payments, timestamp: Date.now() };
            }
            onBackground(result.payments);
          }
        })
        .catch((err) => {
          captureAppError(err, {
            area: "account-cache",
            action: "background-refresh-payment-history",
          });
        });
      return { payments: stale };
    }
  }

  const result = await fetchPaymentHistory();
  if (!result.error && Array.isArray(result.payments)) {
    cachedHistory = { userId, payments: result.payments, timestamp: Date.now() };
  }
  return result;
}

export function invalidatePaymentHistoryCache(): void {
  cachedHistory = null;
}

/** Returns cached payments without triggering a fetch. Used for lazy state init. */
export function peekCachedPaymentHistory(userId: string): PaymentTransaction[] | null {
  if (cachedHistory && cachedHistory.userId === userId) return cachedHistory.payments;
  return null;
}

// ─── Active subscriptions cache ───────────────────────────────────────────────

let cachedSubscriptions: {
  userId: string;
  subscriptions: ActiveSubscription[];
  timestamp: number;
} | null = null;

/**
 * @param userId       Current user's firebase_id. Different user = fresh fetch.
 * @param ttlMs        Freshness window (default 2.5 min).
 * @param onBackground Stale-while-revalidate callback — see getCachedPaymentHistory.
 */
export async function getCachedActiveSubscriptions(
  userId: string,
  ttlMs = SUBSCRIPTIONS_TTL_MS,
  onBackground?: (subscriptions: ActiveSubscription[]) => void,
): Promise<{ subscriptions: ActiveSubscription[]; error?: string | null }> {
  if (cachedSubscriptions && cachedSubscriptions.userId === userId) {
    const isStale = Date.now() - cachedSubscriptions.timestamp >= ttlMs;
    if (!isStale) {
      return { subscriptions: cachedSubscriptions.subscriptions };
    }
    if (onBackground) {
      const stale = cachedSubscriptions.subscriptions;
      const snapshotTimestamp = cachedSubscriptions.timestamp;
      fetchActiveSubscriptions()
        .then((result) => {
          if (!result.error && Array.isArray(result.subscriptions)) {
            if (cachedSubscriptions?.timestamp === snapshotTimestamp && cachedSubscriptions?.userId === userId) {
              cachedSubscriptions = { userId, subscriptions: result.subscriptions, timestamp: Date.now() };
            }
            onBackground(result.subscriptions);
          }
        })
        .catch((err) => {
          captureAppError(err, {
            area: "account-cache",
            action: "background-refresh-subscriptions",
          });
        });
      return { subscriptions: stale };
    }
  }

  const result = await fetchActiveSubscriptions();
  if (!result.error && Array.isArray(result.subscriptions)) {
    cachedSubscriptions = { userId, subscriptions: result.subscriptions, timestamp: Date.now() };
  }
  return result;
}

export function invalidateSubscriptionsCache(): void {
  cachedSubscriptions = null;
}

/** Returns cached subscriptions without triggering a fetch. Used for lazy state init. */
export function peekCachedActiveSubscriptions(userId: string): ActiveSubscription[] | null {
  if (cachedSubscriptions && cachedSubscriptions.userId === userId) return cachedSubscriptions.subscriptions;
  return null;
}

// ─── Rewards cache ────────────────────────────────────────────────────────────

export const REWARDS_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

let cachedRewards: {
  userId: string;
  data: RewardsData;
  timestamp: number;
} | null = null;

export async function getCachedRewards(
  userId: string,
  ttlMs = REWARDS_TTL_MS,
  onBackground?: (data: RewardsData) => void,
): Promise<{ data: RewardsData | null; error?: string | null }> {
  if (cachedRewards && cachedRewards.userId === userId) {
    const isStale = Date.now() - cachedRewards.timestamp >= ttlMs;
    if (!isStale) {
      return { data: cachedRewards.data };
    }
    if (onBackground) {
      const stale = cachedRewards.data;
      const snapshotTimestamp = cachedRewards.timestamp;
      fetchRewards()
        .then((result) => {
          if (!result.error && result.data) {
            if (cachedRewards?.timestamp === snapshotTimestamp && cachedRewards?.userId === userId) {
              cachedRewards = { userId, data: result.data, timestamp: Date.now() };
            }
            onBackground(result.data);
          }
        })
        .catch((err) => {
          captureAppError(err, { area: "account-cache", action: "background-refresh-rewards" });
        });
      return { data: stale };
    }
  }

  const result = await fetchRewards();
  if (!result.error && result.data) {
    cachedRewards = { userId, data: result.data, timestamp: Date.now() };
  }
  return result;
}

export function invalidateRewardsCache(): void {
  cachedRewards = null;
}

// ─── Previous cleaners cache ──────────────────────────────────────────────────

export const PREVIOUS_CLEANERS_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

let cachedPreviousCleaners: {
  userId: string;
  cleaners: PreviousCleaner[];
  timestamp: number;
} | null = null;

export async function getCachedPreviousCleaners(
  userId: string,
  ttlMs = PREVIOUS_CLEANERS_TTL_MS,
): Promise<{ cleaners: PreviousCleaner[] }> {
  if (cachedPreviousCleaners && cachedPreviousCleaners.userId === userId) {
    if (Date.now() - cachedPreviousCleaners.timestamp < ttlMs) {
      return { cleaners: cachedPreviousCleaners.cleaners };
    }
  }

  const result = await getPreviousCleaners();
  if (!result.error && result.cleaners) {
    cachedPreviousCleaners = { userId, cleaners: result.cleaners, timestamp: Date.now() };
    return { cleaners: result.cleaners };
  }

  // Stale fallback — show last-known cleaners rather than blank
  if (cachedPreviousCleaners?.userId === userId) {
    return { cleaners: cachedPreviousCleaners.cleaners };
  }
  return { cleaners: [] };
}

export function invalidatePreviousCleanersCache(): void {
  cachedPreviousCleaners = null;
}

// ─── Saved card cache ─────────────────────────────────────────────────────────

export const SAVED_CARD_TTL_MS = 60 * 60 * 1000; // 1 hour

let cachedSavedCard: {
  userId: string;
  hasSavedCard: boolean;
  timestamp: number;
} | null = null;

export async function getCachedSavedCard(
  userId: string,
  ttlMs = SAVED_CARD_TTL_MS,
): Promise<{ hasSavedCard: boolean }> {
  if (cachedSavedCard && cachedSavedCard.userId === userId) {
    if (Date.now() - cachedSavedCard.timestamp < ttlMs) {
      return { hasSavedCard: cachedSavedCard.hasSavedCard };
    }
  }

  const result = await checkHasSavedCard();
  const has = result.hasSavedCard ?? false;
  cachedSavedCard = { userId, hasSavedCard: has, timestamp: Date.now() };
  return { hasSavedCard: has };
}

export function invalidateSavedCardCache(): void {
  cachedSavedCard = null;
}
